import { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";

import { useEpgData } from "../../hooks/useEpgData";
import { useBackwardPrograms } from "../../hooks/useBackwardPrograms";
import { usePastPerformanceSeries } from "../../hooks/usePastPerformanceSeries";
import { useUserJourneyLogger } from "../../hooks/useUserJourneyLogger";

import PastPerformanceChart from "../../components/charts3/PastPerformanceChart";
import "./Channel.css";

export default function ChannelPastPerformance({ channelId, refreshKey, rawVideo }) {
  const { epgBlocks, loading, error } = useEpgData(channelId, null, {
    startDateRange: dayjs().subtract(30, "day").format("YYYY-MM-DD"),
    endDateRange:   dayjs().format("YYYY-MM-DD"),
  });
  // rawVideo is received as a prop

  const [selectedProgramId, setSelectedProgramId] = useState("");
  const [backwardCount, setBackwardCount] = useState(4);

  /* ---------- UNIQUE PROGRAM LIST ---------- */
  const programOptions = useMemo(() => {
    const map = new Map();
    epgBlocks.forEach((p) => {
      // unique per: program name + weekday + start time + end time
      const key = `${p.programName}__${p.weekdayInt}__${p.startMinute}__${p.endMinute}`;
      if (!map.has(key)) map.set(key, p);
    });
    return Array.from(map.values()).sort(
      (a, b) => a.weekdayInt - b.weekdayInt || a.startMinute - b.startMinute
    );
  }, [epgBlocks]);

  const selectedProgram = useMemo(() => {
    if (!selectedProgramId) return null;
    return programOptions.find((p) => p.id === selectedProgramId) || null;
  }, [selectedProgramId, programOptions]);

  /* ---------- BACKWARD FETCH ---------- */
  const {
    backwardPrograms,
    fetchBackward,
    loading: bwLoading,
  } = useBackwardPrograms(channelId);

  useEffect(() => {
    if (!selectedProgram) return;

    fetchBackward({
      programId: selectedProgram.id,
      weekdayInt: selectedProgram.weekdayInt,
      programStart: selectedProgram.startDate.toISOString(),
      backwardCount,
    });
  }, [selectedProgram, backwardCount, fetchBackward]);

  /* ---------- SERIES ---------- */
  const { labels, seriesSets } = usePastPerformanceSeries({
    rawVideo,
    backwardPrograms,
  });

  /* ---------- LOGGER INTEGRATION ---------- */
  const { logPageChange } = useUserJourneyLogger({
    channelId,
  });
  useEffect(() => {
    logPageChange({
      chartType: "pastperformance",
    });
  }, [logPageChange]);

  return (
    <div className="channel-box">
      <div className="channel-controls-card">

        {error && (
          <div style={{ color: "red", padding: 12 }}>
            {error}
          </div>
        )}

        {loading && !error && (
          <div className="pastperformance-controls-skeleton">
            <div className="pp-skel-input" />
            <div className="pp-skel-input small" />
          </div>
        )}

        {!loading && !error && (
          <div className="pastperformance-controls">

            <div className="control-group wide">
              <label>Select Program</label>
              <select
                value={selectedProgramId}
                onChange={(e) => setSelectedProgramId(e.target.value)}
              >
                <option value="">Select program…</option>
                {programOptions.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.programName}{" "}
                    {String(Math.floor(p.startMinute / 60)).padStart(2, "0")}:{String(p.startMinute % 60).padStart(2, "0")}
                    {" - "}
                    {String(Math.floor(p.endMinute / 60)).padStart(2, "0")}:{String(p.endMinute % 60).padStart(2, "0")}
                    {" | "}
                    {["", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"][p.weekdayInt] || ""}
                  </option>
                ))}
              </select>
            </div>

            <div className="control-group small">
              <label>Program Backward</label>
              <select
                value={backwardCount}
                onChange={(e) => setBackwardCount(Number(e.target.value))}
              >
                {[1,2,3,4,5,6,7,8,9,10,11,12].map((n) => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
            </div>

          </div>
        )}
      </div>

      {/* ================= CHART ================= */}
      <div className="chart-box">

        {/* 🔥 FIRST LOAD SKELETON */}
        {loading && (
          <div className="location-chart-skeleton" />
        )}

        {/* EMPTY STATE */}
        {!loading && !selectedProgram && (
          <div style={{ position: "relative" }}>
            <PastPerformanceChart empty title="Past Performance / Program" />
          </div>
        )}

        {/* 🔥 CHART LOADING */}
        {!loading && selectedProgram && bwLoading && (
          <div className="location-chart-skeleton" />
        )}

        {/* ✅ CHART */}
        {!loading && selectedProgram && !bwLoading && (
          <PastPerformanceChart
            labels={labels}
            seriesSets={seriesSets}
            title="Past Performance / Program"
          />
        )}

      </div>
    </div>
  );
}
